<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('installationEdit.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('nav.installations') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ tabTitle }}</li>
                    </ol>
                </div>
            </div>

            <!-- Tabs -->
            <ul class="nav nav-tabs nav-bordered nav-justified mb-3">
                <li class="nav-item">
                    <router-link :to="`/installation-edit-information?id=${facilityId}`"
                        id="tab0"
                        :class="{ 'nav-link text-primary active': activeTab === 0, 'nav-link': activeTab !== 0 }">
                        <i class="mdi mdi-information d-md-none d-block"></i>
                        <span class="d-none d-md-block">{{ $t('installationEdit.tabs.information') }}</span>
                    </router-link>
                </li>
                <li class="nav-item">
                    <router-link :to="`/installation-edit-components?id=${facilityId}`"
                        id="tab1"
                        :class="{ 'nav-link text-primary active': activeTab === 1, 'nav-link': activeTab !== 1 }">
                        <i class="mdi mdi-file-tree d-md-none d-block"></i>
                        <span class="d-none d-md-block">{{ $t('installationEdit.tabs.components') }}</span>
                    </router-link>
                </li>
                <li class="nav-item">
                    <router-link :to="`/installation-edit-scenarios?id=${facilityId}`"
                        id="tab2"
                        :class="{ 'nav-link text-primary active': activeTab === 2, 'nav-link': activeTab !== 2 }">
                        <i class="mdi mdi-movie-open d-md-none d-block"></i>
                        <span class="d-none d-md-block">{{ $t('installationEdit.tabs.scenarios') }}</span>
                    </router-link>
                </li>
                <li class="nav-item">
                    <router-link :to="`/installation-edit-alerts?id=${facilityId}`"
                        id="tab3"
                        :class="{ 'nav-link text-primary active': activeTab === 3, 'nav-link': activeTab !== 3 }">
                        <i class="mdi mdi-bell-ring d-md-none d-block"></i>
                        <span class="d-none d-md-block">{{ $t('installationEdit.tabs.alerts') }} <span v-if="false" class="badge bg-danger ms-1">3</span></span>
                    </router-link>
                </li>
                <li class="nav-item">
                    <router-link :to="`/installation-edit-rooms?id=${facilityId}`"
                        id="tab4"
                        :class="{ 'nav-link text-primary active': activeTab === 4, 'nav-link': activeTab !== 4 }">
                        <i class="mdi mdi-door d-md-none d-block"></i>
                        <span class="d-none d-md-block">{{ $t('installationEdit.tabs.rooms') }}</span>
                    </router-link>
                </li>
            </ul>
            <router-view></router-view>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { showToast } from '@/services/toast';

export default {
    name: 'InstallationEdit',
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();
        const facilityId = parseInt(route.query.id);
        const activeTab = ref(0);

        const loadComponents = async () => {
            const facility = userStore.facilities.find(f => f.id === facilityId);
            if(facility?.pending_validation) {
                // The administrator has not validated the installation yet
                showToast('info', t('installationRow.pendingValidation'));
                router.replace('/installation-list');
                return;
            }
            await userStore.loadComponents(facilityId);
            await userStore.loadExternalAlarms(facilityId);
            await userStore.loadCardProfiles(facilityId);
            // Redirect to action page
            switch (route.query.action) {
                case 'information':
                    activeTab.value = 0;
                    router.push({ path: '/installation-edit-information', query: { id: facilityId } });
                    break;
                case 'components':
                    activeTab.value = 1;
                    router.push({ path: '/installation-edit-components', query: { id: facilityId } });
                    break;
                case 'scenarios':
                    activeTab.value = 2;
                    router.push({ path: '/installation-edit-scenarios', query: { id: facilityId } });
                    break;
                case 'alerts':
                    activeTab.value = 3;
                    router.push({ path: '/installation-edit-alerts', query: { id: facilityId } });
                    break;
                case 'rooms':
                    activeTab.value = 4;
                    router.push({ path: '/installation-edit-rooms', query: { id: facilityId } });
                    break;
            }
        };

        const updateActiveTabFromRoute = () => {
            const path = route.path;
            if(path === "/installation-edit") {
                router.go(-1);
            } else {
                if (path.includes('installation-edit-information')) {
                    activeTab.value = 0;
                } else if (path.includes('installation-edit-components')) {
                    activeTab.value = 1;
                } else if (path.includes('installation-edit-scenarios')) {
                    activeTab.value = 2;
                } else if (path.includes('installation-edit-alerts')) {
                    activeTab.value = 3;
                } else if (path.includes('installation-edit-rooms')) {
                    activeTab.value = 4;
                }
                if(activeTab.value !== 0) document.getElementById("tab0").blur();
                if(activeTab.value !== 1) document.getElementById("tab1").blur();
                if(activeTab.value !== 2) document.getElementById("tab2").blur();
                if(activeTab.value !== 3) document.getElementById("tab3").blur();
                if(activeTab.value !== 4) document.getElementById("tab4").blur();
            }
        };

        onMounted(() => {
            loadComponents();
        });

        watch(() => route.path, updateActiveTabFromRoute);

        const tabTitle = computed(() => {
            return t(`installationEdit.tabTitles.${activeTab.value}`) || t('installationEdit.tabTitles.0');
        });

        return {
            facilityId,
            activeTab,
            tabTitle,
        };
    },
};
</script>
